import React, { lazy, Suspense, useEffect, useState } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

const DottedSurfaceFallback = () => null;

const DottedSurface = lazy(() =>
  import('./DottedSurface').catch((error) => {
    console.error('[LazyDottedSurface] Failed to load DottedSurface chunk:', error);
    return { default: DottedSurfaceFallback };
  })
);

class LazyDottedSurfaceErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    console.error('[LazyDottedSurface] DottedSurface render failed:', error);
  }

  render() {
    if (this.state.hasError) {
      return null;
    }

    return this.props.children;
  }
}

type LazyDottedSurfaceProps = {
  delay?: number;
  audioReactive?: boolean;
  gyroscopeEnabled?: boolean;
  className?: string;
  sx?: SxProps<Theme>;
};

export function LazyDottedSurface({
  delay = 1000,
  audioReactive = true,
  gyroscopeEnabled = true,
  className,
  sx,
}: LazyDottedSurfaceProps) {
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldRender(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  if (!shouldRender) {
    return null;
  }

  return (
    <LazyDottedSurfaceErrorBoundary>
      <Suspense fallback={null}>
        <DottedSurface
          className={className}
          audioReactive={audioReactive}
          gyroscopeEnabled={gyroscopeEnabled}
          sx={sx}
        />
      </Suspense>
    </LazyDottedSurfaceErrorBoundary>
  );
}

export default LazyDottedSurface;
